export type MessageRole = 'user' | 'assistant'

export interface ThinkingStep {
  id: string
  toolName: string
  label: string
  args: string
  result?: unknown
  status: 'running' | 'done' | 'error'
  durationMs?: number
  thought?: string
  sources?: Array<{ title: string; url?: string; type?: string }>
}

export interface Message {
  id: string
  role: MessageRole
  content: string
  timestamp: Date
  isStreaming?: boolean
  thinking?: ThinkingStep[]
  thoughtDuration?: number
}

export interface Conversation {
  id: string
  title: string
  lastMessage: string
  timestamp: Date
  messageCount: number
  // Idempotency key for source-seeded conversations (e.g. `hubspot-task:<id>` from
  // a dashboard card). null for normal chats.
  externalThreadId?: string | null
}

export type ArtifactType = 'email' | 'sequence' | 'dossier' | 'battlecard' | 'general'

export interface ArtifactVersion {
  version: number
  title: string
  content: string
  author: 'ai' | 'user'
  timestamp: Date
}

export interface Artifact {
  id: string
  type?: ArtifactType
  title: string
  content: string
  version: number
  history: ArtifactVersion[]
  createdAt: Date
  updatedAt: Date
}

export type CrmEntityType = 'lead' | 'contact' | 'deal' | 'kb'

export interface CrmEntity {
  id: string
  type: CrmEntityType
  title: string
  subtitle?: string
  metadata?: Record<string, unknown>
}

export interface FileAttachment {
  id: string
  name: string
  size: number
  type: string
  status: 'parsing' | 'ready' | 'error'
  content?: string
  errorMessage?: string
}

export interface ChoiceOption {
  label: string
  description?: string
  recommended?: boolean
}

export interface ChoiceQuestion {
  header: string
  question: string
  options: ChoiceOption[]
  allowMultiple: boolean
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

// The ask_user_choices tool's result, narrowed at runtime — the result arrives as
// `unknown` off the stream and from saved history, so anything malformed simply
// renders no card rather than crashing the message.
export function parseChoiceQuestions(result: unknown): ChoiceQuestion[] | null {
  if (!isRecord(result) || !Array.isArray(result.questions)) return null

  const questions: ChoiceQuestion[] = []
  for (const q of result.questions) {
    if (!isRecord(q) || typeof q.header !== 'string' || typeof q.question !== 'string' || !Array.isArray(q.options)) {
      return null
    }
    const options: ChoiceOption[] = []
    for (const o of q.options) {
      if (!isRecord(o) || typeof o.label !== 'string') return null
      options.push({
        label: o.label,
        ...(typeof o.description === 'string' ? { description: o.description } : {}),
        ...(o.recommended === true ? { recommended: true } : {}),
      })
    }
    questions.push({ header: q.header, question: q.question, options, allowMultiple: q.allow_multiple === true })
  }
  return questions.length > 0 ? questions : null
}
